import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  recipeListSchema,
  recipeSchema,
  type Recipe,
  type RecipeBulk,
  type RecipeInput,
  type RecipePatch,
} from '@dishly/shared';
import { api, isNetworkError } from './api.ts';
import { hasMirror, localRecipe, localSummaries, mirror } from './mirror.ts';
import { searchMirror } from './offline-search.ts';

/** How long to wait for the server before showing the mirror (a weak signal in the shop). */
const ONLINE_TIMEOUT_MS = 8000;

/**
 * Network first, the device mirror when the server can't be reached (PLAN.md §9). Without a
 * mirror the original error stands, so screens show their offline state.
 */
async function withMirror<T>(online: (signal: AbortSignal) => Promise<T>, local: () => Promise<T>) {
  if (navigator.onLine) {
    try {
      return await online(AbortSignal.timeout(ONLINE_TIMEOUT_MS));
    } catch (err) {
      if (!isNetworkError(err)) throw err;
      if (!(await hasMirror(mirror()))) throw err;
      return local();
    }
  }
  if (!(await hasMirror(mirror()))) throw new TypeError('offline');
  return local();
}

/** Mirrored recipes searched offline (MiniSearch), in the list's default order. */
async function localList(q: string) {
  const db = mirror();
  const summaries = await localSummaries(db);
  if (!q) return summaries;
  const hits = new Set(await searchMirror(db, q));
  return summaries.filter((r) => hits.has(r.id));
}

/** Query keys and fetchers for recipes; reads fall back to the device mirror offline. */
export const recipeKeys = {
  all: ['recipes'] as const,
  list: () => [...recipeKeys.all, 'list'] as const,
  detail: (id: string) => [...recipeKeys.all, 'detail', id] as const,
};

/** The household's recipes, searched by text; facets and sorting apply locally. */
export const recipeListQuery = (q = '') =>
  queryOptions({
    queryKey: [...recipeKeys.list(), q.trim()] as const,
    queryFn: () =>
      withMirror(
        async (signal) =>
          recipeListSchema.parse(
            await api(
              q.trim() ? `/api/recipes?q=${encodeURIComponent(q.trim())}` : '/api/recipes',
              { signal },
            ),
          ).recipes,
        () => localList(q.trim()),
      ),
    placeholderData: (previous) => previous,
  });

export const recipeQuery = (id: string) =>
  queryOptions({
    queryKey: recipeKeys.detail(id),
    queryFn: () =>
      withMirror(
        async (signal) => recipeSchema.parse(await api(`/api/recipes/${id}`, { signal })),
        async () => {
          const recipe = await localRecipe(mirror(), id);
          // Not on this device: the detail screen shows its "not available offline" state.
          if (!recipe) throw new TypeError('offline');
          return recipe;
        },
      ),
  });

export async function saveRecipe(input: RecipeInput, id?: string): Promise<Recipe> {
  const body = JSON.stringify(input);
  const saved = id
    ? await api(`/api/recipes/${id}`, { method: 'PUT', body })
    : await api('/api/recipes', { method: 'POST', body });
  return recipeSchema.parse(saved);
}

/** Small edits from the detail screen; updates the cached recipe optimistically. */
export function usePatchRecipe(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (patch: RecipePatch) =>
      recipeSchema.parse(
        await api(`/api/recipes/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
      ),
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: recipeKeys.detail(id) });
      const previous = queryClient.getQueryData<Recipe>(recipeKeys.detail(id));
      if (previous) {
        const { reviewed, ...rest } = patch;
        queryClient.setQueryData<Recipe>(recipeKeys.detail(id), {
          ...previous,
          ...rest,
          ...(reviewed !== undefined && {
            reviewedAt: reviewed ? new Date().toISOString() : null,
          }),
        });
      }
      return { previous };
    },
    onError: (_err, _patch, context) => {
      if (context?.previous) queryClient.setQueryData(recipeKeys.detail(id), context.previous);
    },
    onSuccess: (recipe) => {
      queryClient.setQueryData(recipeKeys.detail(id), recipe);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: recipeKeys.list() }),
  });
}

export async function deleteRecipe(id: string): Promise<void> {
  await api(`/api/recipes/${id}`, { method: 'DELETE' });
}

/** Marks several recipes reviewed or deletes them (the list's select mode). */
export function useBulkRecipes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (bulk: RecipeBulk) =>
      api<{ changed: number }>('/api/recipes/bulk', { method: 'POST', body: JSON.stringify(bulk) }),
    onSuccess: (_data, { action, ids }) => {
      for (const id of ids) {
        if (action === 'delete') queryClient.removeQueries({ queryKey: recipeKeys.detail(id) });
        else void queryClient.invalidateQueries({ queryKey: recipeKeys.detail(id) });
      }
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: recipeKeys.list() }),
  });
}

export interface UploadedImage {
  id: string;
  width: number;
  height: number;
}

export async function uploadImage(file: File): Promise<UploadedImage> {
  const form = new FormData();
  form.set('file', file);
  return api<UploadedImage>('/api/images', { method: 'POST', body: form });
}
